import { useMemo, useState } from "react";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { Textarea } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import Tag, { ProductivityTag } from "@/components/ui/Tag.jsx";
import { parseImport } from "@/lib/productivityRules.js";

/*
 * Paste a JSON export and import the rules in it.
 *
 * Everything is validated and SHOWN before anything is sent: which rules will be
 * created, which were rejected and why, and which already exist. An import that
 * silently skips half its entries — or worse, half-applies — is how a rule set
 * quietly stops matching what someone thinks it matches.
 *
 * Duplicates are detected on type + pattern rather than name, because that pair
 * is what decides whether two rules compete for the same session.
 */
export default function ImportDrawer({ existing = [], onClose, onImport, saving, error }) {
    const [text, setText] = useState("");

    const parsed = useMemo(
        () => (text.trim() ? parseImport(text, existing) : null),
        [text, existing],
    );

    return (
        <Drawer
            open
            onClose={onClose}
            title="Import rules"
            subtitle="Paste a JSON export"
            size="lg"
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => onImport(parsed.rules)}
                        disabled={!parsed?.rules.length}
                        loading={saving}
                        data-run-import
                    >
                        {parsed?.rules.length
                            ? `Import ${parsed.rules.length} ${parsed.rules.length === 1 ? "rule" : "rules"}`
                            : "Import"}
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                {error && <Callout tone="danger">{error}</Callout>}

                <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={10}
                    data-import-json
                    data-autofocus
                    className="font-mono text-[12px]"
                    placeholder={`[\n  {\n    "name": "YouTube",\n    "type": "domain",\n    "pattern": "youtube.com",\n    "isRegex": false,\n    "globalStatus": "unproductive",\n    "category": "Media"\n  }\n]`}
                    aria-label="Rules JSON"
                />

                {parsed && (
                    <div className="space-y-3">
                        {parsed.errors.length > 0 && (
                            <Callout tone="danger" data-import-errors>
                                <strong className="font-semibold">
                                    {parsed.errors.length} rejected
                                </strong>
                                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                                    {parsed.errors.map((e) => (
                                        <li key={e}>{e}</li>
                                    ))}
                                </ul>
                            </Callout>
                        )}

                        {parsed.duplicates.length > 0 && (
                            <Callout tone="warning" data-import-duplicates>
                                <strong className="font-semibold">
                                    {parsed.duplicates.length} skipped as duplicates
                                </strong>
                                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                                    {parsed.duplicates.map((d) => (
                                        <li key={d}>{d}</li>
                                    ))}
                                </ul>
                            </Callout>
                        )}

                        {parsed.rules.length > 0 && (
                            <div data-import-preview>
                                <p className="mb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
                                    Will be created
                                </p>
                                <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800">
                                    {parsed.rules.map((rule, i) => (
                                        <div
                                            key={`${rule.type}-${rule.pattern}-${i}`}
                                            className="flex min-w-0 items-center gap-2.5 border-b border-slate-100 px-3.5 py-2 last:border-0 dark:border-slate-800"
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[13px] font-medium">
                                                    {rule.name}
                                                </span>
                                                <span className="block truncate font-mono text-[11px] text-slate-400 dark:text-slate-500">
                                                    {rule.pattern}
                                                </span>
                                            </span>
                                            <Tag tone="slate">{rule.type}</Tag>
                                            {rule.isRegex && <Tag tone="violet">regex</Tag>}
                                            <ProductivityTag status={rule.globalStatus} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!parsed.rules.length && !parsed.errors.length && !parsed.duplicates.length && (
                            <p className="text-[13px] text-slate-400 dark:text-slate-500">
                                No rules found in that JSON.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Drawer>
    );
}
